import { formatFreeTilesLayout } from 'common/buyOneGetManyLayout';
import { EBlockType, TStoreBlock } from 'common/TemplateStudio.types';
import { StoreBodyBlock } from 'constants/enums';
import { processRichTextContent } from 'utils/colorUtils';
import type { z } from 'zod';

import type { templateStudioSchema } from './Components/FormPage/validation-schema';
import { v4 } from 'uuid';

type TemplateStudioFormValues = z.infer<typeof templateStudioSchema>;

type BlockFields = Record<string, unknown>;

interface BlockBuilder {
  type: EBlockType;
  build: (values: TemplateStudioFormValues) => BlockFields;
}

const assignIfDefined = (
  fields: BlockFields,
  key: string,
  value: unknown
): void => {
  if (value !== undefined && value !== null) {
    fields[key] = value;
  }
};

const copyBase = (source?: {
  selectedDesignId?: string;
  themes?: unknown;
}): BlockFields => {
  if (!source) {
    return {};
  }
  const fields: BlockFields = {};
  assignIfDefined(fields, 'selectedDesignId', source?.selectedDesignId);
  assignIfDefined(fields, 'themes', source?.themes);
  return fields;
};

const STORE_BODY_BLOCK_TO_BUILDER: Partial<
  Record<StoreBodyBlock, BlockBuilder>
> = {
  [StoreBodyBlock.bundles]: {
    type: EBlockType.BUNDLE,
    build: (values) => {
      const source = values.bundle;
      const fields = copyBase(source);
      if (source?.offersPerRow != null) {
        fields.offersPerRow = Number(source.offersPerRow);
      }
      assignIfDefined(fields, 'bundleViewModel', source?.bundleViewModel);
      return fields;
    }
  },
  [StoreBodyBlock.specialOffers]: {
    type: EBlockType.PROMOTION,
    build: (values) => copyBase(values.promotion)
  },
  [StoreBodyBlock.rollingOffers]: {
    type: EBlockType.ROLLING_OFFER,
    build: (values) => {
      const source = values.rollingOffers;
      const fields = copyBase(source);
      assignIfDefined(fields, 'desktopLayout', source?.desktopLayout);
      return fields;
    }
  },
  [StoreBodyBlock.onePlusMany]: {
    type: EBlockType.ONE_PLUS_MANY,
    build: (values) => {
      const source = values.buyOneGetMany;
      const fields = copyBase(source);
      assignIfDefined(fields, 'desktopLayout', source?.desktopLayout);
      if (source !== undefined) {
        fields.maxOffers = {
          unlimited: source.unlimited,
          value: source.maxOffers
        };
        fields.rewardsLayout = formatFreeTilesLayout(source.freeTilesLayout);
      }
      return fields;
    }
  },
  [StoreBodyBlock.dailyBonusBadge]: {
    type: EBlockType.DAILY_BONUS,
    build: (values) => {
      const fields: BlockFields = {};
      assignIfDefined(
        fields,
        'selectedDesignId',
        values.popup?.selectedDesignId
      );
      assignIfDefined(
        fields,
        'selectedBadgeDesignId',
        values.dailyBonusBadge?.selectedDesignId
      );
      assignIfDefined(fields, 'badgeThemes', values.dailyBonusBadge?.themes);
      return fields;
    }
  },
  [StoreBodyBlock.rewardCalendar]: {
    type: EBlockType.REWARD_CALENDAR,
    build: (values) => {
      const source = values.rewardCalendar;
      const fields = copyBase(source);
      assignIfDefined(
        fields,
        'selectedBadgeDesignId',
        source?.selectedBadgeDesignId
      );
      return fields;
    }
  },
  [StoreBodyBlock.triggeredPopups]: {
    type: EBlockType.TRIGGERED_POPUP,
    build: (values) => {
      const source = values.triggeredPopups;
      const fields = copyBase(source);
      assignIfDefined(
        fields,
        'selectedBadgeDesignId',
        source?.selectedBadgeDesignId
      );
      return fields;
    }
  },
  [StoreBodyBlock.progressBars]: {
    type: EBlockType.PROGRESS_BAR,
    build: (values) => copyBase(values.progressBar)
  },
  [StoreBodyBlock.richText]: {
    type: EBlockType.RICH_TEXT,
    build: (values) => {
      const processed = processRichTextContent(values.richText);
      const fields: BlockFields = {};
      assignIfDefined(fields, 'text', processed?.text);
      assignIfDefined(fields, 'fontColor', processed?.fontColor);
      return fields;
    }
  }
};

interface OrderedEntry {
  block: StoreBodyBlock;
  isVisible: boolean;
}

const toOrderedEntries = (values: TemplateStudioFormValues): OrderedEntry[] => {
  const groupedOrder = values.bodyBlocksOrderV2;
  if (Array.isArray(groupedOrder) && groupedOrder.length > 0) {
    return groupedOrder.flatMap((group) =>
      (group.blocks ?? []).map((block) => ({
        block,
        isVisible: group.isVisible
      }))
    );
  }
  const flatOrder = values.bodyBlocksOrder;
  if (Array.isArray(flatOrder)) {
    return flatOrder.map((block) => ({ block, isVisible: true }));
  }
  return [];
};

export const buildStoreBlocksFromForm = (
  values: TemplateStudioFormValues
): TStoreBlock[] =>
  toOrderedEntries(values).flatMap((entry) => {
    const builder = STORE_BODY_BLOCK_TO_BUILDER[entry.block];
    if (!builder) {
      return [];
    }
    const block = {
      id: `bb-${String(entry.block)}-${v4()}`,
      type: builder.type,
      isVisible: entry.isVisible,
      ...builder.build(values)
    } as unknown as TStoreBlock;
    return [block];
  });
